import React from 'react';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { FanIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import api from './api';
import AssetsActivityCard from './AssetsActivityCard';
import AssetsCustomDataView from './AssetsCustomDataView';
import AssetsEntityBlock from './AssetsEntityBlock';
import AssetsEntityMenuItem from './AssetsEntityMenuItem';
import AssetsPickerEmptyState from './AssetsPickerEmptyState';
import AssetsPickerNoSearchResults from './AssetsPickerNoSearchResults';
import AssetsPickerSearchBox from './AssetsPickerSearchBox';
import AssetsProvider from './AssetsProvider';
import getParsedAssets from './helpers/getParsedAssets';
import getUniqueValues from './helpers/getUniqueValues';
import { Asset } from './types';
import ReferencePickerPlugin from '../../types/ReferencePickerPlugin';

/**
 * @deprecated
 * Assets plugin is deprecated. Use `@adsk/acs-assets-shared-web` instead.
 * See https://wiki.autodesk.com/x/cYgIhw for more information.
 */
const plugin: ReferencePickerPlugin<Asset> = {
  metadata: {
    domain: 'autodesk-bim360-asset',
    type: 'asset',
  },
  renderIcon: (p) => <FanIcon {...p} />,
  toolId: TOOL_IDS.ASSETS,
  showSearchBox: true,
  is3LOSupported: true,
  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('ASSETS');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle: ({ showTabTitle = true }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const value = T.translate('ADD_ASSET_REFERENCE');

    return (
      <>
        {showTabTitle && <TabTitle value={value as string} />}
        {value}
      </>
    );
  },
  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('ADD_ASSETS', { context: count });
  },
  SelectedCount: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('ASSETS_SELECTED', { context: count });
  },
  CustomRemoveEntityModalTitle: () => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('REMOVE_ASSET_REFERENCE_TITLE');
  },
  CustomRemoveEntityModalContent: () => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('REMOVE_ASSET_REFERENCE_CONTENT');
  },
  queryEntities: async ({
    projectId,
    pageLimit,
    filters: { searchExpression, nextUrl },
  }) => {
    const { results: assets, pagination } = await api.queryAssets(
      projectId,
      typeof nextUrl === 'string' ? nextUrl : undefined,
      {
        limit: pageLimit,
        'filter[searchText]': searchExpression,
      },
    );

    if (assets.length === 0) {
      return { data: [] };
    }

    const uniqueCategoriesIds = getUniqueValues(assets, 'categoryId');
    const uniqueStatusesIds = getUniqueValues(assets, 'statusId');

    const [categories, statuses] = await Promise.all([
      api
        .batchGetCategoryMetadata(projectId, uniqueCategoriesIds)
        .catch(() => []),
      api.batchGetStatuses(projectId, uniqueStatusesIds).catch(() => []),
    ]);

    return {
      data: getParsedAssets(assets, categories, statuses),
      nextPageQuery: pagination.nextUrl
        ? {
            projectId,
            pageLimit,
            filters: { nextUrl: pagination.nextUrl },
          }
        : undefined,
    };
  },
  getEntitiesByIds: async ({ projectId, ids }) => {
    const assets = await api.batchGetAssets(projectId, ids);

    const uniqueCategoriesIds = getUniqueValues(assets, 'categoryId');
    const uniqueStatusesIds = getUniqueValues(assets, 'statusId');

    const [categories, statuses] = await Promise.all([
      // we can still show the entities without categories or statuses
      // if fetching any of those fails, catch it and return an empty array instead
      api
        .batchGetCategoryMetadata(projectId, uniqueCategoriesIds)
        .catch(() => []),
      api.batchGetStatuses(projectId, uniqueStatusesIds).catch(() => []),
    ]);

    return { data: getParsedAssets(assets, categories, statuses) };
  },
  Provider: AssetsProvider,
  EntityBlock: AssetsEntityBlock,
  EntityMenuItem: AssetsEntityMenuItem,
  ActivityCard: AssetsActivityCard,
  CustomDataView: AssetsCustomDataView,
  CustomPickerSearchBox: AssetsPickerSearchBox,
  CustomPickerEmptyState: AssetsPickerEmptyState,
  CustomPickerNoSearchResults: AssetsPickerNoSearchResults,
};

export default plugin;
